import { AxiosResponse } from "axios"
import { apiInstance } from "../base"
import { ICreatePartnerDiscount, IGetPartnerBonusTransactionsAggregatesResults, IGetPartnerStoresStatistics, IGetPartnerTransactionResult, IGetPartnerTransactionsResults, IListPartnerDiscounts, IListPartnerTransactionsRequest, IOEMGroup, IPartner, IPartnerAccrual, IPartnerDailyStatistics, IPartnerMonthlyStatistics, IPartnerStatisticsResults, IPartnerStore, IPartnerStoreGeo, IStoresBonusTransactionStatistics, IUpdatePartnerDiscount } from "./types"
import { toISOStringWithTimezone } from "@/shared/lib/to-iso-string-with-timezone"

export const getPartnerStatistics = (
    startDate: Date, 
    endDate: Date, 
    partnerId?: string[]
): Promise<AxiosResponse<{ data: IPartnerStatisticsResults}>> => {
    return apiInstance.handler.get<{data: IPartnerStatisticsResults}>(`/partners/statistics`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getPartners = (): Promise<AxiosResponse<IPartner[]>> => {
    return apiInstance.handler.get<IPartner[]>(`/partners`)
}

export const getStores = (partnerId?: string[] | null): Promise<AxiosResponse<{[key:string]: IPartnerStore[]}>> => {
    return apiInstance.handler.get<{[key:string]: IPartnerStore[]}>(`/partners/stores`, {
        params: {
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getPartnerTransactions = (query: IListPartnerTransactionsRequest): Promise<AxiosResponse<IGetPartnerTransactionsResults>> => {
    return apiInstance.handler.get<IGetPartnerTransactionsResults>(`/partners/transactions`, {
        params: {
            page: query.page,
            limit: query.limit,
            startDate: query.startDate.toISOString(),
            endDate: query.endDate.toISOString(),
            partnerId: query.partnerId?.length ? query.partnerId : null,
            storeId: query.storeId?.length ? query.storeId : null,
            search: query.search?.length ? query.search : null,
            clientType: query.clientType
        }
    })
}

export const getPartnerTransaction = (id: string): Promise<AxiosResponse<IGetPartnerTransactionResult>> => {
    return apiInstance.handler.get<IGetPartnerTransactionResult>(`/partners/transactions/${id}`)
}

export const getPartnerBonusTransactionsAggregates = (
    startDate: Date, 
    endDate: Date, 
    partnerId?: string[] | null
): Promise<AxiosResponse<IGetPartnerBonusTransactionsAggregatesResults>> => {
    return apiInstance.handler.get<IGetPartnerBonusTransactionsAggregatesResults>(`/partners/statistics/bonus-transactions`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getPartnerMonthlyStatistics = (
    startDate: Date,
    partnerId?: string[] | null
): Promise<AxiosResponse<{data: IPartnerMonthlyStatistics[]}>> => {
    return apiInstance.handler.get<{data: IPartnerMonthlyStatistics[]}>(`/partners/statistics/monthly`, {
        params: {
            startDate: toISOStringWithTimezone(startDate).split('T')[0],
            partnerId: partnerId?.length ? partnerId : null
        }
    })  
}

export const getPartnerDailyStatistics = (
    startDate: Date,
    endDate: Date,
    partnerId?: string[] | null
): Promise<AxiosResponse<{data: IPartnerDailyStatistics[]}>> => {
    return apiInstance.handler.get<{data: IPartnerDailyStatistics[]}>(`/partners/statistics/daily`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })  
}

export const getTransactionsReportXlsx = (
    startDate: Date,
    endDate: Date,
    partnerId?: string[] | null
): Promise<AxiosResponse> => {
    return apiInstance.handler.get(`/back-office/get/transactions-report/xlsx`, {
        responseType: 'blob',
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getStoresGeo = (partnerId?: string[] | null): Promise<AxiosResponse<IPartnerStoreGeo[]>> => {
    return apiInstance.handler.get(`/partners/stores-geo`, {
        params: {
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getStoresBonusTransactionsStatistics = (
    startDate: Date, 
    endDate: Date, 
    partnerId?: string[] | null
): Promise<AxiosResponse<IStoresBonusTransactionStatistics>> => {
    return apiInstance.handler.get<IStoresBonusTransactionStatistics>(`/partners/statistics/stores-bonus-transactions`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getOemGroups = (): Promise<AxiosResponse<IOEMGroup[]>> => {
    return apiInstance.handler.get<IOEMGroup[]>(`/partners/oem-groups`)
}

export const getPartnerAccruals = (): Promise<AxiosResponse<IPartnerAccrual[]>> => {
    return apiInstance.handler.get<IPartnerAccrual[]>(`/partners/accruals`)
}

export const createPartnerDiscount = (data: ICreatePartnerDiscount): Promise<AxiosResponse> => {
    return apiInstance.handler.post(`/partners/discounts`, data)
}

export const updatePartnerDiscount = (id: string, data: IUpdatePartnerDiscount): Promise<AxiosResponse> => {
    return apiInstance.handler.put(`/partners/discounts/${id}`, data)
}

export const deletePartnerDiscount = (id: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(`/partners/discounts/${id}`)
}

export const getPartnerDiscounts = (
    page: number, 
    limit: number, 
    partnerId?: string[] | null
): Promise<AxiosResponse<IListPartnerDiscounts>> => {
    return apiInstance.handler.get<IListPartnerDiscounts>(`/partners/discounts`, {
        params: {
            page,
            limit,
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}

export const getPartnerStoresStatistics = (
    startDate: Date, 
    endDate: Date, 
    partnerId?: string[] | null
): Promise<AxiosResponse<IGetPartnerStoresStatistics>> => {
    return apiInstance.handler.get<IGetPartnerStoresStatistics>(`/partners/statistics/stores`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            partnerId: partnerId?.length ? partnerId : null
        }
    })
}
